/**
 * @typedef {{
 *  boundingBox: [[number, number]],
 *  cornerPoints: [[number, number]]
 *  format: string,
 *  rawValue: string
 * }} DecoderResult
 * 
 * @typedef {{
 *  getText: () => string
 *  getBarcodeFormat: () => string
 * }} Result
 * 
 * @typedef {(result: Result, error?: Error) => any} DecodeContinuouslyCallback
 */

/**
 * The wrapper around the build-in BarcodeDetector object
 */
export class NativeBarcodeScanner {

    /**
     * If navigator is present.
     */
    get hasNavigator() {
        return typeof navigator !== 'undefined';
    }

    /**
     * If mediaDevices under navigator is supported.
     */
    get isMediaDevicesSuported() {
        return this.hasNavigator && !!navigator.mediaDevices;
    }

    /**
     * If enumerateDevices under navigator is supported.
     */
    get canEnumerateDevices() {
        return !!(this.isMediaDevicesSuported && navigator.mediaDevices.enumerateDevices);
    }

    static get isNativeBarcodeScannerSupported() {
        let result = false;
        try {
            // @ts-ignore
            let bd = new BarcodeDetector({ formats: ['code_39'] });
            result = !!bd;
        } catch { }
        return result;
    }

    static async getSupportedFormats() {
        return NativeBarcodeScanner.isNativeBarcodeScannerSupported
            // @ts-ignore
            ? await BarcodeDetector.getSupportedFormats()
            : [];
    }

    /**
     * Creates an instance of BrowserCodeReader.
     * @param {string[]} [formats=['code_39', 'codabar', 'ean_13']]
     * @param {number} [timeBetweenScansMillis=200] the time delay between subsequent successful decode tries
     * @param {number} [timeBetweenDecodingAttempts=10] the time delay between decode attempts made by the scanner.
     * @memberOf NativeBarcodeScanner    
     */
    constructor(formats = null, timeBetweenScansMillis = 200, timeBetweenDecodingAttempts = 10) {
        /**
         * This will break the loop.
         */
        this._stopAsyncDecode = false;
        /**
         * The HTML canvas element, used to draw the video or image's frame for decoding.
         * @type {HTMLCanvasElement}
         */
        this.captureCanvas;
        /**
         * The HTML canvas element context.
         * @type {CanvasRenderingContext2D}
         */
        this.captureCanvasContext;
        /**
         * The stream output from camera.
         * @type {MediaStream}
         */
        this.stream;
        /**
         * The HTML video element, used to display the camera stream.
         * @type {HTMLVideoElement}
         */
        this.videoElement;
        /**
         * Should contain the current registered listener for video loaded-metadata,
         * used to unregister that listener when needed.
         * @type {EventListener}
         */
        this.videoCanPlayListener;
        /**
         * Should contain the current registered listener for video play-ended,
         * used to unregister that listener when needed.
         * @type {EventListener}
         */
        this.videoEndedListener;
        /**
         * Should contain the current registered listener for video playing,
         * used to unregister that listener when needed.
         * @type {EventListener}
         */
        this.videoPlayingEventListener;

        this.timeBetweenScansMillis = timeBetweenScansMillis;
        this.timeBetweenDecodingAttempts = timeBetweenDecodingAttempts;

        if (!Array.isArray(formats) || formats.length === 0) {
            formats = ['code_39', 'codabar', 'ean_13'];
        }

        // @ts-ignore
        this.barcodeDetector = new BarcodeDetector({ formats });
    }

    /**
     * Lists all the available video input devices.
     * @returns {Promise<MediaDeviceInfo[]>}
     */
    async listVideoInputDevices() {

        if (!this.hasNavigator) {
            throw new Error('Can\'t enumerate devices, navigator is not present.');
        }
        if (!this.canEnumerateDevices) {
            throw new Error('Can\'t enumerate devices, method not supported.');
        }

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = [];
        for (const device of devices) {
            const kind = device.kind;
            if (kind !== 'videoinput') {
                continue;
            }

            const deviceId = device.deviceId
            const label = device.label || `Video device ${videoDevices.length + 1}`;
            const groupId = device.groupId;
            const videoDevice = { deviceId, label, kind, groupId };

            videoDevices.push(videoDevice);
        }

        return videoDevices;
    }

    /**
     * Continuously tries to decode the barcode from the device specified by device while showing the video in the specified video element.
     * @param {string|null} deviceId the id of one of the devices obtained after calling listVideoInputDevices. Can be undefined, in this case it will decode from one of the available devices, preffering the main camera (environment facing) if available.
     * @param {HTMLVideoElement|null} videoSource the video element in page where to show the video while decoding. Can be either an element id or directly an HTMLVideoElement. Can be undefined, in which case no video will be shown.
     * @param {DecodeContinuouslyCallback} callbackFn
     * @returns {Promise<void>}
     * @memberOf NativeBarcodeScanner    
     */
    async decodeFromVideoDevice(deviceId, videoSource, callbackFn) {
        /** @type {MediaTrackConstraints} */
        let videoConstraints;
        if (!deviceId) {
            videoConstraints = { facingMode: 'environment' };
        } else {
            videoConstraints = { deviceId: { exact: deviceId } };
        }

        /** @type {MediaStreamConstraints} */
        const constraints = { video: videoConstraints };
        return await this.decodeFromConstraints(constraints, videoSource, callbackFn);
    }

    /**
     * Continuously tries to decode the barcode from a stream obtained from the given constraints while showing the video in the specified video element.
     * @param {MediaStreamConstraints} constraints the media stream constraints to get s valid media stream to decode from
     * @param {HTMLVideoElement} videoSource the video element in page where to show the video while decoding. Can be either an element id or directly an HTMLVideoElement. Can be undefined, in which case no video will be shown.
     * @param {DecodeContinuouslyCallback} callbackFn
     * @returns {Promise<void>} The decoding result.
     * @memberOf NativeBarcodeScanner    
     */
    async decodeFromConstraints(constraints, videoSource, callbackFn) {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        return await this.decodeFromStream(stream, videoSource, callbackFn);
    }

    /**
     * In one attempt, tries to decode the barcode from a stream obtained from the given constraints while showing the video in the specified video element.
     * @param {MediaStream} stream the media stream to get s valid media stream to decode from
     * @param {HTMLVideoElement} videoSource the video element in page where to show the video while decoding. Can be either an element id or directly an HTMLVideoElement. Can be undefined, in which case no video will be shown.
     * @param {DecodeContinuouslyCallback} callbackFn
     * @returns {Promise<void>} The decoding result.
     * @memberOf NativeBarcodeScanner    
     */
    async decodeFromStream(stream, videoSource, callbackFn) {
        this.reset();
        const video = await this.attachStreamToVideo(stream, videoSource);
        return this.decodeContinuously(video, callbackFn);
    }

    /**
     * Breaks the decoding loop.
     */
    stopContinuousDecode() {
        this._stopContinuousDecode = true;
    }

    /**
     * Sets the new stream and request a new decoding-with-delay.
     * @param {MediaStream} stream The stream to be shown in the video element.
     * @param {HTMLVideoElement} videoSource,
     * @returns {Promise<HTMLVideoElement>}
     */
    async attachStreamToVideo(stream, videoSource) {
        const videoElement = this.prepareVideoElement(videoSource);
        this.addVideoSource(videoElement, stream);

        this.videoElement = videoElement;
        this.stream = stream;

        await this.playVideoOnLoadAsync(videoElement);
        return videoElement;
    }

    /**
     * @param {HTMLVideoElement} videoElement
     * @returns {Promise<void>}
     */
    playVideoOnLoadAsync(videoElement) {
        return new Promise((resolve, reject) => this.playVideoOnLoad(videoElement, () => resolve()));
    }

    /**
     * Binds listeners and callbacks to the videoElement.
     * @param {HTMLVideoElement} element
     * @param {Function} callbackFn
     */
    playVideoOnLoad(element, callbackFn) {
        this.videoEndedListener = () => this.stopStreams();
        this.videoCanPlayListener = () => this.tryPlayVideo(element);
        this.videoPlayingEventListener = () => callbackFn();

        element.addEventListener('ended', this.videoEndedListener);
        element.addEventListener('canplay', this.videoCanPlayListener);
        element.addEventListener('playing', this.videoPlayingEventListener);

        // if canplay was already fired, we won't know when to play, so just give it a try
        this.tryPlayVideo(element);
    }

    /**
     * Checks if the given video element is currently playing.
     * @param {HTMLVideoElement} video
     * @returns {boolean}
     */
    isVideoPlaying(video) {
        return video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
    }

    /**
     * Just tries to play the video and logs any errors.
     * The play call is only made is the video is not already playing.
     * @param {HTMLVideoElement} videoElement
     * @returns {Promise<void>}
     */
    async tryPlayVideo(videoElement) {
        if (this.isVideoPlaying(videoElement)) {
            console.warn('Trying to play video that is already playing.');
            return;
        }
        try {
            await videoElement.play();
        } catch (e) {
            console.warn('It was not possible to play the video.');
        }
    }

    /**
     * Sets a HTMLVideoElement for scanning or creates a new one.
     * @param {HTMLVideoElement} videoSource The HTMLVideoElement to be set.
     * @returns {HTMLVideoElement}
     */
    prepareVideoElement(videoSource) {
        /** @type {HTMLVideoElement} */
        let videoElement;
        if (!videoSource && typeof document !== 'undefined') {
            videoElement = document.createElement('video');
            videoElement.width = 200;
            videoElement.height = 200;
        }
        if (videoSource instanceof HTMLVideoElement) {
            videoElement = videoSource;
        }

        // Needed for iOS 11
        videoElement.setAttribute('autoplay', 'true');
        videoElement.setAttribute('muted', 'true');
        videoElement.setAttribute('playsinline', 'true');

        return videoElement;
    }

    /**
     * Continuously decodes from video input.
     * @param {HTMLVideoElement} element
     * @param {DecodeContinuouslyCallback} callbackFn
     */
    decodeContinuously(element, callbackFn) {
        this._stopContinuousDecode = false;

        const loop = async () => {
            if (this._stopContinuousDecode) {
                this._stopContinuousDecode = undefined;
                return;
            }

            try {
                /** @type {[DecoderResult]} */
                const r = await this.decode(element);
                /** @type {Result} */
                const result = {
                    getText: () => r[0] ? r[0].rawValue : '',
                    getBarcodeFormat: () => r[0] ? r[0].format : '',
                };
                callbackFn(result, null);
                setTimeout(loop, this.timeBetweenScansMillis);
            } catch (e) {
                callbackFn(null, e);
                setTimeout(loop, this.timeBetweenDecodingAttempts);
            }
        };

        loop();
    }

    /**
     * Decode from the video
     * @param {HTMLVideoElement} element
     * @returns {Promise<[DecoderResult]>}
     */
    async decode(element) {
        const imageData = this.getImageData(element);
        return await this.barcodeDetector.detect(imageData);
    }

    /**
     * Gets an ImageData in some image source.
     * @param {HTMLVideoElement} videoElement
     * @returns {ImageData}
     */
    getImageData(videoElement) {
        const ctx = this.getCaptureCanvasContext(videoElement);
        this.drawImageOnCanvas(ctx, videoElement);

        const canvas = this.getCaptureCanvas(videoElement);
        return ctx.getImageData(0, 0, canvas.width, canvas.height);
    }

    /**
     * @param {HTMLVideoElement} videoElement
     * @returns {CanvasRenderingContext2D}
     */
    getCaptureCanvasContext(videoElement) {
        if (!this.captureCanvasContext) {
            const elem = this.getCaptureCanvas(videoElement);
            const ctx = elem.getContext('2d');
            this.captureCanvasContext = ctx;
        }
        return this.captureCanvasContext;
    }

    /**
     * Gets the cartupe canvas
     * @param {HTMLVideoElement} videoElement
     * @returns {HTMLCanvasElement}
     */
    getCaptureCanvas(videoElement) {
        if (!this.captureCanvas) {
            const elem = this.createCaptureCanvas(videoElement);
            this.captureCanvas = elem;
        }
        return this.captureCanvas;
    }

    /**
     * Ovewriting this allows you to manipulate the snapshot image in anyway you want before decode.
     * @param {CanvasRenderingContext2D} canvasElementContext
     * @param {HTMLVideoElement} srcElement
     */
    drawImageOnCanvas(canvasElementContext, srcElement) {
        canvasElementContext.drawImage(srcElement, 0, 0);
    }

    /**
     * Prepares the canvas for capture and scan frames.
     * @param {HTMLVideoElement} videoElement
     * @returns {HTMLCanvasElement}
     */
    createCaptureCanvas(videoElement) {
        if (typeof document === 'undefined') {
            this._destroyCaptureCanvas();
            return null;
        }

        const canvasElement = document.createElement('canvas');

        let width;
        let height;
        if (typeof videoElement !== 'undefined' && videoElement instanceof HTMLVideoElement) {
            width = videoElement.videoWidth;
            height = videoElement.videoHeight;
        }

        canvasElement.style.width = width + 'px';
        canvasElement.style.height = height + 'px';
        canvasElement.width = width;
        canvasElement.height = height;

        return canvasElement;
    }

    /**
     * Stops the continuous scan and cleans the stream.
     */
    stopStreams() {
        if (this.stream) {
            this.stream.getVideoTracks().forEach(t => t.stop());
            this.stream = undefined;
        }
        if (this._stopContinuousDecode === false) {
            this.stopContinuousDecode();
        }
    }

    /**
     * Resets the code reader to the initial state. Cancels any ongoing barcode scanning from video or camera.
     * @memberOf NativeBarcodeScanner    
     */
    reset() {
        // stops the camera, preview and scan
        this.stopStreams();

        // clean and forget about HTML elements
        this._destroyVideoElement();
        this._destroyCaptureCanvas();
    }

    _destroyVideoElement() {
        if (!this.videoElement) {
            return;
        }

        // first gives freedon to the element
        if (typeof this.videoEndedListener !== 'undefined') {
            this.videoElement.removeEventListener('ended', this.videoEndedListener);
        }
        if (typeof this.videoPlayingEventListener !== 'undefined') {
            this.videoElement.removeEventListener('playing', this.videoPlayingEventListener);
        }
        if (typeof this.videoCanPlayListener !== 'undefined') {
            this.videoElement.removeEventListener('canplay', this.videoCanPlayListener);
        }
        this.cleanVideoSource(this.videoElement);
        this.videoElement = undefined;
    }

    /**
     * Cleans canvas references
     */
    _destroyCaptureCanvas() {
        this.captureCanvasContext = undefined;
        this.captureCanvas = undefined;
    }

    /**
     * Defines what the videoElement src will be.
     * @param {HTMLVideoElement} videoElement
     * @param {MediaStream} stream
     */
    addVideoSource(videoElement, stream) {
        // Older browsers may not have `srcObject`
        try {
            // @note Throws Exception if interrupted by a new loaded request
            videoElement.srcObject = stream;
        } catch (err) {
            // @note Avoid using this in new browsers, as it is going away.
            videoElement.src = URL.createObjectURL(stream);
        }
    }

    /**
     * Unbinds a HTML video src property.
     * @param {HTMLVideoElement} videoElement
     */
    cleanVideoSource(videoElement) {
        try {
            videoElement.srcObject = null;
        } catch (err) {
            videoElement.src = '';
        }
        this.videoElement.removeAttribute('src');
    }
}
